/**
 * Dedicated IndexedDB store for pending petition upload blobs.
 *
 * Blobs here are AUTHORITATIVE user data (unlike documentCache, which is a
 * discardable cache). They live until the backend confirms the upload via
 * `/principal/confirm` or `/anexos/confirm`. Scoped per userId to prevent
 * cross-account leakage (invariant of the offline-first architecture).
 */
import { openDB, type IDBPDatabase } from 'idb';

import { getCurrentUserId, getUserIdOrNull } from './userScope';

const DB_VERSION = 1;
const STORE_NAME = 'pending-blobs';
const DB_PREFIX = 'jurisapp-pending-petition-blobs-';

export interface PendingBlobMetadata {
  /** Related outbox draft key (one-to-many: one draft, many blobs). */
  draftKey: string;
  /** "principal" | "anexo:<ordem>". */
  kind: string;
  /** User-facing file name. */
  nome: string;
  /** Original MIME type. */
  contentType: string;
  /** Byte size of the blob. */
  size: number;
  /** When it was queued. */
  createdAt: string;
}

export interface PendingBlobEntry {
  /** Unique blob reference used as a pointer in the outbox entry payload. */
  blob_ref: string;
  blob: Blob;
  metadata: PendingBlobMetadata;
}

function getDbName(): string {
  return `${DB_PREFIX}${getCurrentUserId()}`;
}

let dbInstance: IDBPDatabase | null = null;
let currentDbName: string | null = null;

async function getDB(): Promise<IDBPDatabase> {
  const dbName = getDbName();

  if (dbInstance && currentDbName !== dbName) {
    dbInstance.close();
    dbInstance = null;
  }
  if (dbInstance) return dbInstance;

  currentDbName = dbName;
  dbInstance = await openDB(dbName, DB_VERSION, {
    upgrade(db) {
      const store = db.createObjectStore(STORE_NAME, { keyPath: 'blob_ref' });
      store.createIndex('by-draft', 'metadata.draftKey');
    },
  });
  return dbInstance;
}

function makeBlobRef(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `blob-${crypto.randomUUID()}`;
  }
  return `blob-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/**
 * Persists a blob and returns a stable reference to be stored in the outbox entry.
 * Throws if no user is authenticated (authoritative data requires ownership).
 */
export async function putBlob(
  blob: Blob,
  metadata: Omit<PendingBlobMetadata, 'createdAt' | 'size'>,
): Promise<string> {
  if (!getUserIdOrNull()) {
    throw new Error('Não é possível guardar arquivo sem usuário autenticado');
  }
  const db = await getDB();
  const blob_ref = makeBlobRef();
  const fullMeta: PendingBlobMetadata = {
    ...metadata,
    size: blob.size,
    createdAt: new Date().toISOString(),
  };
  await db.put(STORE_NAME, { blob_ref, blob, metadata: fullMeta });
  return blob_ref;
}

export async function getBlob(blob_ref: string): Promise<PendingBlobEntry | null> {
  if (!getUserIdOrNull()) return null;
  try {
    const db = await getDB();
    const entry = (await db.get(STORE_NAME, blob_ref)) as PendingBlobEntry | undefined;
    return entry ?? null;
  } catch {
    return null;
  }
}

export async function deleteBlob(blob_ref: string): Promise<void> {
  if (!getUserIdOrNull()) return;
  try {
    const db = await getDB();
    await db.delete(STORE_NAME, blob_ref);
  } catch {
    // Degrade silently
  }
}

export async function listByDraftKey(draftKey: string): Promise<PendingBlobEntry[]> {
  if (!getUserIdOrNull()) return [];
  try {
    const db = await getDB();
    const all = (await db.getAllFromIndex(
      STORE_NAME,
      'by-draft',
      draftKey,
    )) as PendingBlobEntry[];
    return all;
  } catch {
    return [];
  }
}

export async function deleteByDraftKey(draftKey: string): Promise<void> {
  const entries = await listByDraftKey(draftKey);
  for (const entry of entries) {
    await deleteBlob(entry.blob_ref);
  }
}

/** Closes the singleton DB connection. Call before deleting the DB (logout/user change). */
export async function closePendingBlobs(): Promise<void> {
  if (dbInstance) {
    dbInstance.close();
    dbInstance = null;
  }
}
